import { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { Fade, Grid } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { getTitleDetails } from '@/redux/slices/titleSlice';
import { clearCache, fetchTitleChildrenAndVersionsV2, fetchTitleV2, setActiveTitleId, setRootTitleId } from '@/redux/slices/titleV2Slice';
import { resetIngestManagement, setRegisterAgainst, setSelectedRecentTitles, setSelectedTitles } from '@/redux/slices/ingestManagementSlice';
import {
  selectIngestManagementRegisterAgainst,
  selectIngestManagementSelectedRecentTitles,
  selectIngestManagementSelectedTitles
} from '@/redux/selectors/ingestManagementSelectors';
import { selectTitleLoading } from '@/redux/selectors/titleSelectors';
import { selectActiveTitle, selectIsActiveTitleLoading } from '@/redux/selectors/titleV2Selectors';

import { Spinner } from '@/components/_common';
import TitleSearch from '@/components/_common/TitleVersionSelect/TitleSearch';
import RecentTitles from '@/components/IngestManagement/FileIngest/TitleAssociationV2/RecentTitles';
import SelectedParentTitle from '@/components/IngestManagement/FileIngest/TitleAssociationV2/SelectedParentTitle';
import RegisterAgainst from '@/components/IngestManagement/FileIngest/TitleAssociationV2/RegisterAgainst';
import SelectedTitle from '@/components/IngestManagement/FileIngest/TitleAssociationV2/SelectedTitle';

export type SelectedTitlesV2 = { options: TitleEntityV2[]; value: TitleEntityV2 | null }[];

interface TitleAssociationV2Props {
  onRegisterEntitySelect: (entity: TitleEntityV2, titles: SelectedTitlesV2) => void;
  handleClearForm: () => void;
}

// when a parent title (selectedParent) is selected, display the "register against" options (child_entity_types)
// when a "register against" option is selected, display the hierarchy selection dropdowns
// if "series" or "feature" (or any top-level entity) is selected, no dropdowns should be displayed, and we are done and can call onRegisterEntitySelect
// otherwise, display the dropdowns until we reach the `registerAgainst` entity type

const TitleAssociationV2 = ({ onRegisterEntitySelect, handleClearForm }: TitleAssociationV2Props) => {
  const { disableRecentTitles } = useContext(ConfigContext).ingestManagementConfig;
  const dispatch = useAppDispatch();
  const selectedParentTitle = useAppSelector(selectActiveTitle);
  const isTitleLoading = useAppSelector(selectTitleLoading);
  const isActiveTitleLoading = useAppSelector(selectIsActiveTitleLoading);
  const selectedTitles = useAppSelector(selectIngestManagementSelectedTitles) as SelectedTitlesV2;
  const selectedRecentTitles = useAppSelector(selectIngestManagementSelectedRecentTitles);
  const registerAgainst = useAppSelector(selectIngestManagementRegisterAgainst);
  const [showSearch, setShowSearch] = useState(true);

  const handleTitleSelectParent = (title: TitleEntity | TitleEntityV2) => {
    setShowSearch(false);
    dispatch(setRegisterAgainst(null));
    dispatch(setSelectedTitles([]));
    dispatch(setRootTitleId(title.id));
    dispatch(setActiveTitleId(title.id));
    // still need to fetch title v1 to get child entity types
    // not possible with v2, need to do a fetch from parent to leaf (version) node
    // just to get all possible entity types which is very costly
    dispatch(getTitleDetails(title.id));
    dispatch(fetchTitleV2(title.id));
  };

  const handleTitleChange = async (title: TitleEntityV2, index: number) => {
    let newSelectedTitles: SelectedTitlesV2 = [];

    if (title.entity_type === registerAgainst) {
      const st = [
        { options: [selectedParentTitle], value: selectedParentTitle },
        ...selectedTitles.slice(0, index).concat({ options: (selectedTitles[index]?.options || []) as TitleEntityV2[], value: title })
      ];
      onRegisterEntitySelect(title, st as SelectedTitlesV2);
    } else {
      const { children } = await dispatch(fetchTitleChildrenAndVersionsV2(title.id)).unwrap();
      if (children) {
        newSelectedTitles = [...selectedTitles];
        if (newSelectedTitles.length > index + 1) {
          newSelectedTitles[index + 1] = { options: children, value: null };
        } else {
          newSelectedTitles.push({ options: children, value: null });
        }
        dispatch(setSelectedTitles(newSelectedTitles as SelectedTitlesV2));
      }
    }

    if (!disableRecentTitles && selectedRecentTitles) {
      dispatch(setSelectedTitles(selectedRecentTitles));
      dispatch(setSelectedRecentTitles(null));
    } else {
      if (newSelectedTitles.length === 0) {
        newSelectedTitles = [...selectedTitles];
      }

      const updatedSelectedTitles = newSelectedTitles.with(index, {
        options: (newSelectedTitles[index]?.options || []) as TitleEntityV2[],
        value: title
      });
      dispatch(setSelectedTitles(updatedSelectedTitles));
    }
  };

  const handleReset = () => {
    dispatch(clearCache());
    dispatch(resetIngestManagement());
    setShowSearch(true);
  };

  const handleRecentTitleClick = (titles: SelectedTitlesV2) => {
    if (selectedTitles.length && titles.at(-1)?.value?.id === selectedTitles.at(-1)?.value?.id) return;
    const title = titles[0].value;
    if (title) handleTitleSelectParent(title);
    dispatch(setSelectedRecentTitles(titles.slice(1, titles.length)));
  };

  return (
    <div>
      <RecentTitles selectedTitle={selectedTitles.at(-1)?.value?.id} onRecentTitleClick={handleRecentTitleClick} />
      <Fade in={showSearch} timeout={300} unmountOnExit>
        <div>
          <TitleSearch onTitleSelect={handleTitleSelectParent} />
        </div>
      </Fade>
      <Fade in={!showSearch && !!selectedParentTitle} timeout={300} unmountOnExit>
        <SelectedParentWrapper>
          <SelectedParentTitle onReset={handleReset} />
          {isTitleLoading || isActiveTitleLoading ? (
            <SpinnerWrapper style={{ height: 50, width: '100%' }}>
              <Spinner contained size={30} />
            </SpinnerWrapper>
          ) : (
            <RegisterAgainst onRegisterEntityChange={onRegisterEntitySelect} onTitleChange={handleTitleChange} handleClearForm={handleClearForm} />
          )}
          <Grid container alignItems="center" spacing={2} pt={0.5}>
            {isTitleLoading || isActiveTitleLoading ? (
              <Grid item xs={12} display="flex" justifyContent="center" py={4}>
                <Spinner contained size={30} />
              </Grid>
            ) : registerAgainst && selectedTitles.length > 0 ? (
              selectedTitles.map((title, index) => (
                <SelectedTitle key={title.value?.id || `selected-title-${index}`} index={index} title={title} onTitleChange={handleTitleChange} />
              ))
            ) : null}
          </Grid>
        </SelectedParentWrapper>
      </Fade>
    </div>
  );
};

export default TitleAssociationV2;

const SelectedParentWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(1)
}));

const SpinnerWrapper = styled.div(() => ({
  height: 50,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));
